import { OutlookAgencyConsentKind } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { CopyableLink } from "components/shared/copyable-link";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import React from "react";
import { useProductName } from "store/selectors/branding";
import { stableQueryOptionsRpc } from "util/requests";

const approvalSummary: Record<OutlookAgencyConsentKind, string> = {
    send: "sending email from Outlook",
    mailboxSync: "syncing Outlook mailboxes",
};

interface OutlookAgencyConsentProps {
    kind: OutlookAgencyConsentKind;
}

export const OutlookAgencyConsent: React.FC<OutlookAgencyConsentProps> = props => {
    const { kind } = props;
    const [isOpen, setIsOpen] = React.useState(false);
    const productName = useProductName();
    const trpcClient = trpc.useTRPC();
    const linksQuery = useQuery({
        ...trpcClient.outlookAdminConsent.getAgencyLinks.queryOptions(),
        ...stableQueryOptionsRpc(),
    });
    const link = linksQuery.data?.find(candidate => candidate.kind === kind);

    if (!link) {
        return null;
    }

    return (
        <div className="pt-4 text-sm">
            <InteractiveTextLink onClick={() => setIsOpen(!isOpen)}>
                Get Microsoft 365 admin approval for your agency
            </InteractiveTextLink>
            {isOpen ? (
                <div className="pt-2 space-y-2">
                    <p className="text-gray-600">
                        Opening this link as a Microsoft 365 administrator approves{" "}
                        {productName} for {approvalSummary[kind]} across your whole
                        agency, so people aren&apos;t blocked one at a time. Everyone
                        still connects their own mailbox afterward. If you don&apos;t
                        administer Microsoft 365, send the link to whoever does.
                    </p>
                    <CopyableLink
                        url={link.url}
                        copiedLabel="Link copied — paste it in an email or chat"
                    />
                </div>
            ) : null}
        </div>
    );
};
